/**
 * `<page-analytics>` — the query analytics dashboard, routed at
 * `/instances/:id/analytics`.
 *
 * Implements `SC-F808`: query volume over time, the decision breakdown, top tables
 * and top subjects, the truncation rate, the duration distribution and the error-code
 * ranks — **all derived from ingested audit records and never from Prometheus**.
 * That is not an implementation shortcut: Sluice's metric catalogue has no
 * query-latency histogram, no per-query REST counter, no reload counter, no
 * per-datasource health gauge and no budget gauge, while an audit record carries the
 * subject, the tables, the catalogs, the applied policies, the decision, the error
 * code, the duration, the row count and the truncation flag. `SC-S808`'s last
 * criterion is that this screen *says* so, and it does.
 *
 * Also implements `SC-F809`'s entry point (every panel drills through to the explorer
 * pre-filtered, which is how a denial count becomes a denial), `SC-F821`'s profile
 * badge, and `SC-F823`'s sibling link.
 *
 * Four things this page refuses to smooth over:
 *
 *   - **The window.** Every tile and every chart states the window it covers, and the
 *     totals are the sum of the plotted buckets — so a tile and its chart cannot
 *     disagree.
 *   - **The ingest watermark.** The region beyond it is hatched and labelled, never
 *     drawn as zero. Without that, a reader at 14:33 sees a cliff at the right edge
 *     and concludes traffic stopped (`SC-S806`).
 *   - **Which tier answered.** `source: "daily"` means the analytics worker was
 *     unreachable and the numbers came from the per-day rollup — coarser in four
 *     separate ways, each of which the response states and this page repeats.
 *   - **Filters the analytical tier cannot apply.** One filter bar drives the
 *     explorer and the dashboard, and the two tiers do not carry the same columns.
 *     What was dropped is named rather than silently discarded.
 *
 * Stories: `SC-S806`, `SC-S808`, `SC-S809`, `SC-S812`, `SC-S907`.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import "./analytics-charts.js";
// `SC-F818`, mounted below the dashboard: it covers the same window and reports the
// same ingest watermark, so it belongs on this screen rather than beside it.
import "./consumption-panel.js";
// `SC-F813`, mounted below the consumption panel: it is the gateway's OWN process
// counters and is deliberately last, because the question it answers only arises
// once a reader has been told everything above comes from audit records.
import "./metrics-panel.js";
import {
  decisionVariant,
  type BnSelectChangeDetail,
  type BnSelectOption,
} from "../components/index.js";

import {
  AUDIT_DECISIONS,
  type AuditDecision,
  type AuditFilter,
  type AuditProfileName,
} from "../api/audit.js";
import {
  ANALYTICS_INTERVALS,
  getBreakdown,
  getTimeseries,
  toAnalyticsFilter,
  type AnalyticsDimension,
  type AnalyticsInterval,
  type AnalyticsSource,
  type Breakdown,
  type Timeseries,
  type TimeseriesPoint,
} from "../api/analytics.js";
import { getInstance } from "../api/instances.js";
import { ApiError, parseError } from "../api/errors.js";
import { hrefFor, navigate } from "../nav.js";
import type { BucketClickDetail, RankItemClickDetail, RankRow } from "./analytics-charts.js";
import {
  RANGE_PRESETS,
  SOURCE_META,
  auditSharedCss,
  compact,
  count,
  errorCard,
  errorCodeOptions,
  fromAnalyticsWatermark,
  profileBadge,
  rangeFor,
  rangeLabel,
  sourceBadge,
  watermarkLine,
} from "./audit-shared.js";

/** Slot is one panel's result: the data, or the reason there is none. Each panel
 * carries its own failure so one unreachable dimension does not take the page down —
 * "this panel needs the analytics worker" is more useful than a 503 over everything. */
interface Slot<T> {
  data: T | null;
  error: ApiError | null;
}

const emptySlot = <T>(): Slot<T> => ({ data: null, error: null });

/** sum totals a series. The API zero-fills every bucket in the range, so a sum over
 * the plotted points is the window's total and a tile built from it cannot disagree
 * with the chart beside it. */
function sum(points: TimeseriesPoint[] | undefined): number {
  return (points ?? []).reduce((a, p) => a + p[1], 0);
}

/** peak is the largest bucket value. A percentile does not sum, so a latency tile
 * reports the worst bucket and says that is what it is. */
function peak(points: TimeseriesPoint[] | undefined): number {
  const vals = (points ?? []).map((p) => p[1]);
  return vals.length === 0 ? 0 : Math.max(...vals);
}

/** INTERVAL_AUTO is the picker's sentinel for "let the server derive it". It is
 * not an interval and is never sent. */
const INTERVAL_AUTO = "auto";

/** first is the ungrouped series of a timeseries response. */
function first(ts: Timeseries | null): TimeseriesPoint[] {
  return ts?.series[0]?.points ?? [];
}

@customElement("page-analytics")
export class PageAnalytics extends LitElement {
  @property() instanceId = "";

  @state() private profile: AuditProfileName | "" = "";
  /** The filter, shared in spelling with the explorer so one bar drives both. */
  @state() private filter: AuditFilter = {};
  @state() private presetId = "7d";
  /** Empty means "let the server pick the finest interval that fits the window",
   * which is what a chart wants and what the presets imply. */
  @state() private interval: AnalyticsInterval | "" = "";
  @state() private errorCodes: BnSelectOption[] = [];

  @state() private loading = true;
  /** Named separately from `loading` so a refetch dims the previous render rather
   * than replacing it with a skeleton. */
  @state() private refetching = false;
  @state() private fatal: ApiError | null = null;
  @state() private dropped: string[] = [];

  @state() private queries: Slot<Timeseries> = emptySlot();
  @state() private denied: Slot<Timeseries> = emptySlot();
  @state() private truncated: Slot<Timeseries> = emptySlot();
  @state() private latency: Slot<{ p50: Timeseries; p95: Timeseries; p99: Timeseries }> = emptySlot();
  @state() private decisions: Slot<Breakdown> = emptySlot();
  @state() private tables: Slot<Breakdown> = emptySlot();
  @state() private subjects: Slot<Breakdown> = emptySlot();
  @state() private codes: Slot<Breakdown> = emptySlot();
  @state() private policies: Slot<Breakdown> = emptySlot();

  private seq = 0;
  private inFlight?: AbortController;

  static override styles = [
    auditSharedCss,
    css`
      .kpis {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
        gap: var(--bino-space-md);
        margin-bottom: var(--bino-space-md);
      }
      .charts {
        /* One chart per row. A 720x200 viewBox in a half-width column renders
           roughly 80px tall, which is a sparkline pretending to be a chart —
           these three are the page's centrepiece and each gets the full measure. */
        display: grid;
        grid-template-columns: 1fr;
        gap: var(--bino-space-md);
        margin-bottom: var(--bino-space-md);
      }
      .panels {
        display: grid;
        /* 24rem rather than 22: a fully qualified catalog.schema.table name is the
           widest thing on this page, and the ranked panels are where a reader
           compares them character by character. */
        grid-template-columns: repeat(auto-fit, minmax(24rem, 1fr));
        gap: var(--bino-space-md);
      }
      .card-note {
        margin: var(--bino-space-sm) 0 0;
        font-size: var(--bino-font-size-sm);
        color: var(--bino-text-secondary);
      }
      .card-head {
        display: flex;
        align-items: center;
        gap: var(--bino-space-sm);
        flex-wrap: wrap;
        margin-bottom: var(--bino-space-sm);
      }
      .panel-error {
        font-size: var(--bino-font-size-md);
        color: var(--bino-text);
      }
      bn-panel {
        margin-top: var(--bino-space-md);
      }
    `,
  ];

  /* ── lifecycle ───────────────────────────────────────────────────────── */

  override connectedCallback() {
    super.connectedCallback();
    this.readUrl();
    void getInstance(this.instanceId)
      .then((i) => (this.profile = i.audit_profile))
      .catch(() => (this.profile = ""));
    void errorCodeOptions().then((o) => (this.errorCodes = o));
    void this.load();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.inFlight?.abort();
  }

  /** readUrl restores the scope and the filter, so a dashboard view is a link — the
   * same property the explorer's filter set has, and for the same reason. */
  private readUrl() {
    const p = new URLSearchParams(location.search);
    const preset = p.get("preset") ?? "7d";
    const known = RANGE_PRESETS.find((r) => r.id === preset);
    const from = p.get("from") ?? undefined;
    const to = p.get("to") ?? undefined;
    if (known && !(from && to)) {
      this.presetId = known.id;
      const r = rangeFor(known.hours);
      this.filter = { from: r.from, to: r.to };
    } else {
      this.presetId = known && from && to ? known.id : "custom";
      this.filter = { from, to };
    }
    const iv = p.get("interval") ?? "";
    if ((ANALYTICS_INTERVALS as readonly string[]).includes(iv)) {
      this.interval = iv as AnalyticsInterval;
    }
    const list = (k: string) => (p.getAll(k).length ? p.getAll(k) : undefined);
    this.filter = {
      ...this.filter,
      subject: list("subject"),
      decision: list("decision") as AuditDecision[] | undefined,
      error_code: list("error_code"),
      table: list("table"),
      catalog: list("catalog"),
      policy: list("policy"),
    };
  }

  private writeUrl() {
    const p = new URLSearchParams();
    p.set("preset", this.presetId);
    if (this.presetId === "custom") {
      if (this.filter.from) p.set("from", this.filter.from);
      if (this.filter.to) p.set("to", this.filter.to);
    }
    if (this.interval) p.set("interval", this.interval);
    for (const key of ["subject", "decision", "error_code", "table", "catalog", "policy"] as const) {
      for (const v of (this.filter[key] ?? []) as string[]) p.append(key, v);
    }
    navigate(`${location.pathname}?${p.toString()}`, { replace: true });
  }

  /**
   * load fetches every panel in parallel, guarded by a sequence number.
   *
   * `SC-S808`'s fourth criterion is that rapid filter changes cannot render a stale
   * answer: eleven requests go out per view and the network is free to answer them in
   * any order, so a superseded response is dropped rather than painted. The previous
   * render is held at reduced opacity in the meantime — no skeleton, no layout jump.
   */
  private async load() {
    const mine = ++this.seq;
    this.inFlight?.abort();
    const ac = new AbortController();
    this.inFlight = ac;
    const hadData = !!this.queries.data;
    this.refetching = hadData;
    this.loading = !hadData;
    this.fatal = null;

    const from = this.filter.from ?? rangeFor(24 * 7).from;
    const to = this.filter.to ?? rangeFor(24 * 7).to;
    const { filter, dropped } = toAnalyticsFilter(this.filter);
    const iv = this.interval || undefined;
    const signal = ac.signal;

    const ts = (metric: Parameters<typeof getTimeseries>[1]["metric"]) =>
      getTimeseries(this.instanceId, { metric, from, to, interval: iv, filter, signal });
    const bd = (dimension: AnalyticsDimension) =>
      getBreakdown(this.instanceId, { dimension, from, to, limit: 20, filter, signal });

    const settled = await Promise.allSettled([
      ts("queries"),
      ts("denied"),
      ts("truncated"),
      ts("duration_p50"),
      ts("duration_p95"),
      ts("duration_p99"),
      bd("decision"),
      bd("table"),
      bd("subject"),
      bd("error_code"),
      bd("policy"),
    ]);
    if (mine !== this.seq) return;

    const take = <T>(i: number): Slot<T> => {
      const r = settled[i];
      if (r && r.status === "fulfilled") return { data: r.value as T, error: null };
      return { data: null, error: r ? parseError(r.reason) : null };
    };

    this.dropped = dropped;
    this.queries = take<Timeseries>(0);
    this.denied = take<Timeseries>(1);
    this.truncated = take<Timeseries>(2);
    const p50 = take<Timeseries>(3);
    const p95 = take<Timeseries>(4);
    const p99 = take<Timeseries>(5);
    this.latency =
      p50.data && p95.data && p99.data
        ? { data: { p50: p50.data, p95: p95.data, p99: p99.data }, error: null }
        : { data: null, error: p50.error ?? p95.error ?? p99.error };
    this.decisions = take<Breakdown>(6);
    this.tables = take<Breakdown>(7);
    this.subjects = take<Breakdown>(8);
    this.codes = take<Breakdown>(9);
    this.policies = take<Breakdown>(10);

    // Every panel failing the same way is a page-level condition — an unreachable
    // index, a Customer-held instance, a role refusal — and one banner is the honest
    // rendering of it. A single panel failing stays inside that panel.
    if (settled.every((r) => r.status === "rejected")) {
      this.fatal = this.queries.error;
    }
    this.loading = false;
    this.refetching = false;
  }

  private refilter() {
    this.writeUrl();
    void this.load();
  }

  /* ── drill-through ───────────────────────────────────────────────────── */

  /** explorerHref builds a pre-filtered explorer link from the current scope plus an
   * override. Every panel drills through this one function, so the dashboard and the
   * explorer cannot end up disagreeing about what a filter is called. */
  private explorerHref(extra: AuditFilter = {}): string {
    const merged: AuditFilter = { ...this.filter, ...extra };
    const p = new URLSearchParams();
    p.set("preset", "custom");
    if (merged.from) p.set("from", merged.from);
    if (merged.to) p.set("to", merged.to);
    for (const key of [
      "subject",
      "decision",
      "error_code",
      "table",
      "catalog",
      "policy",
    ] as const) {
      for (const v of (merged[key] ?? []) as string[]) p.append(key, v);
    }
    return `${hrefFor("instances", this.instanceId, "audit")}?${p.toString()}`;
  }

  private drillBucket(e: CustomEvent<BucketClickDetail>, extra: AuditFilter = {}) {
    navigate(this.explorerHref({ ...extra, from: e.detail.from, to: e.detail.to }));
  }

  private drillDimension(dimension: AnalyticsDimension, value: string) {
    const key: Partial<Record<AnalyticsDimension, keyof AuditFilter>> = {
      decision: "decision",
      error_code: "error_code",
      subject: "subject",
      catalog: "catalog",
      table: "table",
      policy: "policy",
    };
    const k = key[dimension];
    if (!k) return;
    navigate(this.explorerHref({ [k]: [value] } as AuditFilter));
  }

  /* ── controls ────────────────────────────────────────────────────────── */

  private renderToolbar() {
    const presetOptions: BnSelectOption[] = [
      ...RANGE_PRESETS.map((r) => ({ value: r.id, label: r.label })),
      { value: "custom", label: "Custom range" },
    ];
    // `bn-select` reads an empty `value` as "nothing selected" and shows its
    // placeholder, so "automatic" needs a real value rather than the empty string
    // the API takes. INTERVAL_AUTO is that sentinel and it is mapped back at the
    // edge; it never reaches a request.
    const intervalOptions: BnSelectOption[] = [
      {
        value: INTERVAL_AUTO,
        label: "Automatic",
        description: "The finest bucket width that fits the window under the 2000-bucket cap.",
      },
      ...ANALYTICS_INTERVALS.map((i) => ({ value: i, label: i })),
    ];
    return html`<div class="toolbar">
      <bn-field class="field-md" label="Time range">
        <bn-select
          .options=${presetOptions}
          .value=${this.presetId}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            this.presetId = e.detail.value;
            const known = RANGE_PRESETS.find((r) => r.id === this.presetId);
            if (known) {
              const r = rangeFor(known.hours);
              this.filter = { ...this.filter, from: r.from, to: r.to };
            }
            this.refilter();
          }}
        ></bn-select>
      </bn-field>
      <bn-field class="field-sm" label="Bucket width">
        <bn-select
          .options=${intervalOptions}
          .value=${this.interval || INTERVAL_AUTO}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            const v = e.detail.value;
            this.interval = v === INTERVAL_AUTO ? "" : (v as AnalyticsInterval);
            this.refilter();
          }}
        ></bn-select>
      </bn-field>
      <bn-field class="field-md" label="Decision">
        <bn-select
          multiple
          placeholder="Any decision"
          .options=${AUDIT_DECISIONS.map((d) => ({ value: d, label: d }))}
          .values=${(this.filter.decision ?? []) as string[]}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            this.filter = {
              ...this.filter,
              decision: e.detail.values.length ? (e.detail.values as AuditDecision[]) : undefined,
            };
            this.refilter();
          }}
        ></bn-select>
      </bn-field>
      <bn-field class="field-md" label="Error code">
        <bn-select
          multiple
          filterable
          placeholder="Any code"
          .options=${this.errorCodes}
          .values=${this.filter.error_code ?? []}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            this.filter = {
              ...this.filter,
              error_code: e.detail.values.length ? e.detail.values : undefined,
            };
            this.refilter();
          }}
        ></bn-select>
      </bn-field>
      <bn-field class="field-grow" label="Subject">
        <bn-input
          type="search"
          placeholder="Subject id"
          .value=${(this.filter.subject ?? [])[0] ?? ""}
          @change=${(e: CustomEvent<{ value: string }>) => {
            const v = e.detail.value.trim();
            this.filter = { ...this.filter, subject: v ? [v] : undefined };
            this.refilter();
          }}
        ></bn-input>
      </bn-field>
      <bn-field class="field-grow" label="Table">
        <bn-input
          type="search"
          placeholder="catalog.schema.table"
          .value=${(this.filter.table ?? [])[0] ?? ""}
          @change=${(e: CustomEvent<{ value: string }>) => {
            const v = e.detail.value.trim();
            this.filter = { ...this.filter, table: v ? [v] : undefined };
            this.refilter();
          }}
        ></bn-input>
      </bn-field>
      <div class="toolbar-end">
        <bn-button size="sm" ?loading=${this.refetching} @click=${() => void this.load()}>
          Refresh
        </bn-button>
        <bn-button size="sm" @click=${() => navigate(this.explorerHref())}>Open in explorer</bn-button>
      </div>
    </div>`;
  }

  /* ── frame ───────────────────────────────────────────────────────────── */

  private get windowLabel(): string {
    const ts = this.queries.data;
    return ts ? rangeLabel(ts.from, ts.to) : rangeLabel(this.filter.from, this.filter.to);
  }

  private renderSourceNotice(): TemplateResult | typeof nothing {
    const source: AnalyticsSource | undefined = this.queries.data?.source;
    if (!source || source === "detail" || source === "rollup") return nothing;
    if (source === "empty") {
      return html`<p class="notice" role="status">
        <strong>Nothing has been compacted for this instance yet.</strong> These charts are a
        zero-filled shape rather than an error, and that is not the same as “no traffic”. Read them
        with the ingest watermark above.
      </p>`;
    }
    return html`<p class="notice notice-warn" role="status">
      <strong>Served from the daily rollup — the analytics worker was unreachable.</strong>
      ${SOURCE_META.daily.note} Four consequences to read these numbers with: the x-axis is one day
      per point whatever bucket width you asked for, grouping is limited to the decision, the top-N
      lists were already capped per day before they were stored (so “top 20” is the top 20 of the
      stored top 20s), and <code>duration_p99</code> is the stored <strong>maximum</strong> — an
      upper bound, not a percentile.
    </p>`;
  }

  private renderDroppedNotice(): TemplateResult | typeof nothing {
    if (this.dropped.length === 0) return nothing;
    return html`<p class="notice notice-warn" role="status">
      <strong>Not every filter applies here.</strong> The analytical tier has no column for
      <code>${this.dropped.join("</code>, <code>")}</code>, so ${this.dropped.length === 1
        ? "it was"
        : "they were"}
      <strong>not applied</strong> to the numbers below. The explorer can filter on
      ${this.dropped.length === 1 ? "it" : "them"} because it reads the record index. Saying so beats
      charting a wider slice than the filter bar claims.
    </p>`;
  }

  /* ── tiles ───────────────────────────────────────────────────────────── */

  private decisionCount(d: AuditDecision): number {
    return this.decisions.data?.items.find((i) => i.value === d)?.count ?? 0;
  }

  private renderKpis() {
    const total = sum(first(this.queries.data));
    const deniedTotal = sum(first(this.denied.data));
    const truncTotal = sum(first(this.truncated.data));
    const rate = total > 0 ? `${((truncTotal / total) * 100).toFixed(1)}% of queries` : "";
    const p95Peak = peak(first(this.latency.data?.p95 ?? null));
    const daily = this.queries.data?.source === "daily";
    const win = this.windowLabel;
    const incomplete =
      this.queries.data && !this.queries.data.ingest.complete
        ? "Window extends past the ingest watermark."
        : "";
    return html`<div class="kpis">
      <an-stat
        label="Queries"
        value=${compact(total)}
        window=${win}
        note=${incomplete}
        ?dimmed=${this.refetching}
      ></an-stat>
      <an-stat
        label="Denied"
        tone="deny"
        value=${compact(deniedTotal)}
        window=${win}
        note=${total > 0 ? `${((deniedTotal / total) * 100).toFixed(1)}% of queries` : ""}
        ?dimmed=${this.refetching}
      ></an-stat>
      <an-stat
        label="Rejected"
        tone="deny"
        value=${compact(this.decisionCount("reject"))}
        window=${win}
        note="Refused by a QueryRejectPolicy rule."
        ?dimmed=${this.refetching}
      ></an-stat>
      <an-stat
        label="Errors"
        tone="warn"
        value=${compact(this.decisionCount("error"))}
        window=${win}
        note="A gateway fault, not a policy decision."
        ?dimmed=${this.refetching}
      ></an-stat>
      <an-stat
        label="Truncated results"
        value=${compact(truncTotal)}
        window=${win}
        note=${rate}
        ?dimmed=${this.refetching}
      ></an-stat>
      <an-stat
        label="Peak p95 latency"
        value=${p95Peak ? `${compact(p95Peak)} ms` : "—"}
        window=${win}
        note=${daily
          ? "From the daily rollup: interpolated, not exact."
          : "The worst bucket in the window — a percentile does not sum."}
        ?dimmed=${this.refetching}
      ></an-stat>
    </div>`;
  }

  /* ── charts ──────────────────────────────────────────────────────────── */

  private renderChart(
    heading: string,
    slot: Slot<Timeseries>,
    body: (ts: Timeseries) => TemplateResult,
    note: TemplateResult | typeof nothing = nothing,
  ) {
    return html`<bn-card heading=${heading}>
      ${slot.error
        ? html`<div role="alert">
            <p class="panel-error">${slot.error.message}</p>
            <p class="meta">
              <code>${slot.error.code}</code>${slot.error.requestId
                ? html` · request ${slot.error.requestId}`
                : nothing}
            </p>
          </div>`
        : slot.data
          ? html`<div class="card-head">
                <span class="meta">${rangeLabel(slot.data.from, slot.data.to)}</span>
                <bn-badge variant="neutral">${slot.data.interval} buckets</bn-badge>
                ${sourceBadge(slot.data.source)}
              </div>
              ${body(slot.data)}${note}`
          : html`<bn-spinner block label=${`Loading ${heading}`}></bn-spinner>`}
    </bn-card>`;
  }

  private renderCharts() {
    return html`<div class="charts">
      ${this.renderChart(
          "Queries over time",
          this.queries,
          (ts) => html`<an-columns
            label="Queries"
            tone="neutral"
            drillable
            ?dimmed=${this.refetching}
            .points=${first(ts)}
            .interval=${ts.interval}
            .asOf=${ts.ingest.as_of}
            @bucket-click=${(e: CustomEvent<BucketClickDetail>) => this.drillBucket(e)}
          ></an-columns>`,
          html`<p class="card-note">
            Click a bar — or a row of its data table — to open the explorer on that bucket. Every
            bucket in the range is present and zero-filled, so a quiet hour is a zero and not a gap in
            the axis.
          </p>`,
        )}
      ${this.renderChart(
        "Refusals over time",
        this.denied,
        (ts) => html`<an-columns
          label="Denied"
          tone="deny"
          drillable
          ?dimmed=${this.refetching}
          .points=${first(ts)}
          .interval=${ts.interval}
          .asOf=${ts.ingest.as_of}
          @bucket-click=${(e: CustomEvent<BucketClickDetail>) =>
            this.drillBucket(e, { decision: ["deny"] })}
        ></an-columns>`,
        html`<p class="card-note">
          Denials only — the <code>denied</code> metric. Rejections and gateway errors have their own
          tiles above; they are different outcomes with different fixes, so they are not summed into
          one “failures” line. A click opens the explorer filtered to <code>decision=deny</code> for
          that bucket.
        </p>`,
      )}
      <bn-card heading="Latency distribution">
        ${this.latency.error
          ? html`<div role="alert">
              <p class="panel-error">${this.latency.error.message}</p>
              <p class="meta"><code>${this.latency.error.code}</code></p>
            </div>`
          : this.latency.data
            ? html`
                <div class="card-head">
                  <span class="meta">${this.windowLabel}</span>
                  <bn-badge variant="neutral">${this.latency.data.p50.interval} buckets</bn-badge>
                  ${sourceBadge(this.latency.data.p50.source)}
                </div>
                <an-lines
                  label="Query duration"
                  unit=" ms"
                  ?dimmed=${this.refetching}
                  .interval=${this.latency.data.p50.interval}
                  .asOf=${this.latency.data.p50.ingest.as_of}
                  .series=${[
                    { label: "p50", tone: "neutral" as const, points: first(this.latency.data.p50) },
                    { label: "p95", tone: "warn" as const, points: first(this.latency.data.p95) },
                    { label: "p99", tone: "deny" as const, points: first(this.latency.data.p99) },
                  ]}
                ></an-lines>
                <p class="card-note">
                  ${this.latency.data.p99.source === "daily"
                    ? html`<strong>p99 is the stored maximum here.</strong> The daily rollup keeps
                        p50, p95 and max but no p99, so the top line is an upper bound rather than a
                        percentile. It is served as an over-estimate deliberately: returning zero
                        would draw a p99 below the p95, which looks plausible and is worse.`
                    : html`Percentiles do not sum, so there is no total for this chart — the tile
                        above reports the worst bucket. On a range of a week or more the numbers come
                        from the hourly rollup's histogram by interpolation, which the tier badge
                        names.`}
                </p>
              `
            : html`<bn-spinner block label="Loading the latency distribution"></bn-spinner>`}
      </bn-card>
    </div>`;
  }

  /* ── ranked panels ───────────────────────────────────────────────────── */

  /**
   * renderRanked is one top-N panel.
   *
   * The one thing it must never do is render an empty list as "nothing happened".
   * Under `source: "daily"` the rollup has no column for `error_code`, `catalog` or
   * `policy`, so those dimensions come back **legitimately empty with the tier
   * named** — and the honest reading is "this panel needs the analytics worker".
   */
  private renderRanked(
    heading: string,
    dimension: AnalyticsDimension,
    slot: Slot<Breakdown>,
    opts: { keyLabel: string; tone?: "neutral" | "deny"; note?: TemplateResult } = {
      keyLabel: "Value",
    },
  ) {
    const b = slot.data;
    const rows: RankRow[] =
      b?.items.map((i) =>
        dimension === "decision"
          ? {
              value: i.value,
              count: i.count,
              denied: i.denied,
              badge: html`<bn-badge .variant=${decisionVariant(i.value)}>${i.value}</bn-badge> `,
            }
          : { value: i.value, count: i.count, denied: i.denied },
      ) ?? [];
    const unavailable =
      b !== null && b.items.length === 0 && b.source === "daily" && dimension !== "decision";
    return html`<bn-card heading=${heading}>
      ${slot.error
        ? html`<div role="alert">
            <p class="panel-error">${slot.error.message}</p>
            <p class="meta">
              <code>${slot.error.code}</code>${slot.error.requestId
                ? html` · request ${slot.error.requestId}`
                : nothing}
            </p>
          </div>`
        : !b
          ? html`<bn-spinner block label=${`Loading ${heading}`}></bn-spinner>`
          : unavailable
            ? html`<p class="notice notice-warn">
                <strong>This panel needs the analytics worker.</strong> The per-day rollup that is
                answering right now carries no <code>${dimension}</code> column, so the list is empty
                because the tier cannot express the question — <em>not</em> because nothing happened.
              </p>`
            : html`
                <div class="card-head">
                  <span class="meta">${rangeLabel(b.from, b.to)}</span>
                  ${sourceBadge(b.source)}
                  ${b.truncated
                    ? html`<bn-badge variant="warning"
                        >top ${rows.length} of ${count(b.distinct)}</bn-badge
                      >`
                    : nothing}
                </div>
                <an-ranks
                  .rows=${rows}
                  keyLabel=${opts.keyLabel}
                  valueLabel="Records"
                  tone=${opts.tone ?? "neutral"}
                  ?showDenied=${dimension !== "decision"}
                  drillable
                  ?dimmed=${this.refetching}
                  @item-click=${(e: CustomEvent<RankItemClickDetail>) =>
                    this.drillDimension(dimension, e.detail.value)}
                ></an-ranks>
                ${b.unnested
                  ? html`<p class="card-note">
                      This dimension is an array on the record, so one query counts against every
                      value it touched — the column legitimately sums to more than the query total.
                    </p>`
                  : nothing}
                ${b.truncated
                  ? html`<p class="card-note">
                      ${b.source === "daily"
                        ? "Always truncated on the daily tier: the source lists were capped per day before they were stored, so this is the top of the stored tops rather than the top of the window."
                        : `${count(b.distinct)} distinct values exist in this window; the ranking shows the first ${rows.length}.`}
                    </p>`
                  : nothing}
                ${opts.note ?? nothing}
              `}
    </bn-card>`;
  }

  private renderPanels() {
    return html`<div class="panels">
      ${this.renderRanked("Decisions", "decision", this.decisions, {
        keyLabel: "Decision",
        note: html`<p class="card-note">
          All four outcomes, with the same badge tones the explorer and every policy screen use.
          <code>deny</code> and <code>reject</code> share a tone deliberately — both are refusals —
          and the label is what tells them apart, which is why this is a ranked table and not a
          four-colour stack.
        </p>`,
      })}
      ${this.renderRanked("Top tables", "table", this.tables, {
        keyLabel: "Table",
        note: html`<p class="card-note">
          Fully qualified <code>catalog.schema.table</code> names, with how many of each were denied —
          which is what turns a traffic list into an access-review starting point. It answers what
          <em>was</em> touched; it cannot answer who <em>may</em> touch it, and no dashboard can: an
          identity that never ran a query leaves no record.
        </p>`,
      })}
      ${this.renderRanked("Top subjects", "subject", this.subjects, { keyLabel: "Subject" })}
      ${this.renderRanked("Error codes", "error_code", this.codes, {
        keyLabel: "Error code",
        tone: "deny",
        note: html`<p class="card-note">
          Transport rate-limit refusals are <strong>not here and not anywhere</strong>: Sluice
          deliberately does not audit them, because at that point in the request there is no subject
          yet. <code>ERR_BUDGET_EXCEEDED</code> and the query-path <code>ERR_RATE_LIMITED</code> do
          reach the audit trail and are counted; the transport half is metrics-only and needs
          <code>SLUICE-U07</code>.
        </p>`,
      })}
      ${this.renderRanked("Policies firing", "policy", this.policies, {
        keyLabel: "Policy",
        note: html`<p class="card-note">
          A policy appears here when its selector matched, which is not the same as it having changed
          anything — a policy can apply and change nothing. Which columns a
          <code>ColumnMaskPolicy</code> actually masked is not in any record
          (<code>SLUICE-U24</code>): this answers at policy granularity and says so.
        </p>`,
      })}
    </div>`;
  }

  /* ── render ──────────────────────────────────────────────────────────── */

  override render() {
    if (this.loading) {
      return html`<header>
          <div class="head-text"><h1>Analytics</h1></div>
        </header>
        <bn-card><bn-spinner block label="Loading analytics"></bn-spinner></bn-card>`;
    }
    if (this.fatal) {
      return html`<header>
          <div class="head-text"><h1>Analytics</h1></div>
        </header>
        ${errorCard(
          "Could not load analytics",
          this.fatal,
          () => void this.load(),
          html`<p class="meta">
            Analytics is derived from ingested audit records, so an unreachable audit index takes this
            screen with it — and that is a control-plane condition, not a statement about your
            instance. Your instance's own audit volume is unaffected and remains the record of
            authority. Under the Customer-held profile there are no records in the cloud at all, and
            this screen has nothing to compute from by design.
          </p>`,
        )}`;
    }
    const ingest = this.queries.data?.ingest;
    return html`
      <header>
        <div class="head-text">
          <h1>Analytics</h1>
          <p class="lede">
            What this gateway did, computed from the audit records it wrote. Every number here is a
            count over ingested records — not a Prometheus metric — which is why the subject, the
            table, the policy and the error code are all available to filter on.
          </p>
        </div>
        <div class="head-actions">
          ${profileBadge(ingest?.profile || this.profile)}
          ${sourceBadge(this.queries.data?.source)}
          <bn-button @click=${() => navigate(hrefFor("instances", this.instanceId, "audit"))}>
            Audit explorer
          </bn-button>
        </div>
      </header>

      ${ingest ? watermarkLine(fromAnalyticsWatermark(ingest)) : nothing}
      ${this.renderSourceNotice()} ${this.renderDroppedNotice()} ${this.renderToolbar()}
      ${this.renderKpis()} ${this.renderCharts()} ${this.renderPanels()}

      <!-- SC-F818. It shares this page's window and its watermark, and it is here
           rather than on a surface of its own because the question it answers is the
           one the decision breakdown raises when ERR_BUDGET_EXCEEDED appears in it. -->
      <consumption-panel
        .instanceId=${this.instanceId}
        .from=${this.filter.from ?? ""}
        .to=${this.filter.to ?? ""}
      ></consumption-panel>

      <!-- SC-F813. Last on the page, and last for a reason: it is the gateway's own
           process metrics and is never the source of anything above it. -->
      <metrics-panel .instanceId=${this.instanceId}></metrics-panel>

      <bn-panel eyebrow="Where these numbers come from">
        <p>
          Every chart and every count on this page is derived from <strong>ingested audit
          records</strong>, never from the instance's Prometheus metrics. That is a deliberate
          choice: Sluice's metric catalogue has no query-latency histogram, no per-query REST counter,
          no reload counter, no per-datasource health gauge and no budget gauge — while an audit
          record carries the subject, the tables, the catalogs, the applied policies, the decision,
          the error code, the duration, the row count and the truncation flag. The instance's own
          process metrics are on this page too, in their own panel above — read on demand, thin by
          design, and never the source of these numbers.
        </p>
        <p class="meta">
          Four questions this page structurally cannot answer, so that they are not discovered later:
          which columns a mask resolved to on a given query (the record names the policy, never the
          columns); what SQL actually ran after rewriting (there is no such field — only whether the
          shape changed); how many requests were rate-limited at the transport and by whom (not
          audited, because there is no subject yet); and which identities <em>can</em> read a table
          (nobody querying it is not evidence that nobody may — that is a prospective question and
          needs an effective-access review, not a dashboard).
        </p>
      </bn-panel>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-analytics": PageAnalytics;
  }
}
